import { Request, Response } from "express";
import mongoose, { Types } from "mongoose";

import {
  handleUploadError,
  processUploadedFiles,
} from "../middleware/upload-middleware";
import { Personal, School, Student, Teacher, User } from "../models";
import { PROFILE_APPROVAL_STATUS } from "../models/constants/model-constants";
import { logger } from "../utils/logger";
import {
  personalProfileValidation,
  schoolProfileValidation,
  studentProfileValidation,
  teacherProfileValidation,
  updatePersonalProfileValidation,
} from "../validation/profile-validation";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Interface for authenticated request
interface AuthenticatedRequest extends Request {
  user: {
    userId: string;
    email: string;
    role: string;
    isEmailVerified: boolean;
    isProfileCompleted: boolean;
    status: string;
  };
}

function trimPhoneLike(v: unknown): string {
  return typeof v === "string" || typeof v === "number" ? String(v).trim() : "";
}

/** Joi accepts phoneNumber alias; Mongoose profiles only persist `phone`. */
function applyPhoneNumberAlias(value: Record<string, unknown>): void {
  const pnStr = trimPhoneLike(value.phoneNumber);
  const pStr = trimPhoneLike(value.phone);
  if (pnStr !== "" && pStr === "") {
    value.phone = pnStr;
  }
  delete value.phoneNumber;
}

class ProfileController {
  /**
   * Create personal profile (for school users)
   */
  async createPersonalProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only school users can create personal profiles
      if (userRole !== "school") {
        res.status(403).json({
          success: false,
          message: "Only school users can create personal profiles",
        } as APIResponse);
        return;
      }

      // Check if user already has a personal profile
      const existingUser = await User.findById(userId);
      if (!existingUser) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      const existingPersonal = await Personal.findOne({ userId });
      if (existingPersonal) {
        res.status(409).json({
          success: false,
          message: "Personal profile already exists",
        } as APIResponse);
        return;
      }

      // Parse form data for complex fields
      const formData = { ...req.body };

      // Parse address if it's a JSON string
      if (formData.address && typeof formData.address === "string") {
        try {
          formData.address = JSON.parse(formData.address);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      // DEBUG: Log what we're about to validate
      console.log("🔍 PERSONAL PROFILE VALIDATION:");
      console.log(
        "formData before validation:",
        JSON.stringify(formData, null, 2),
      );

      // Validate request body using personal profile validation
      const { error, value } = personalProfileValidation.validate(formData, {
        allowUnknown: true, // Allow extra fields
        stripUnknown: true, // Remove unknown fields
      });
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      // Handle age to dateOfBirth conversion
      if (value.age && !value.dateOfBirth) {
        const currentYear = new Date().getFullYear();
        const birthYear = currentYear - Number.parseInt(value.age);
        value.dateOfBirth = new Date(birthYear, 0, 1); // January 1st of birth year
        delete value.age; // Remove the age field after conversion
      }

      // Convert flat address fields to nested address object for database storage
      if (
        value.address ||
        value.city ||
        value.state ||
        value.country ||
        value.postalCode
      ) {
        const addressObj = {
          street: value.address || "",
          city: value.city || "",
          state: value.state || "",
          country: value.country || "",
          postalCode: value.postalCode || "",
        };
        value.address = addressObj;

        // Remove the flat fields after converting to nested object
        delete value.city;
        delete value.state;
        delete value.country;
        delete value.postalCode;
      }

      // Process uploaded files
      const files = (req as any).files ?? { profileImage: [(req as any).file] };
      const fileData = processUploadedFiles(files);

      // Create personal profile
      const personal = new Personal({
        userId,
        ...value,
        ...fileData,
      });

      await personal.save();

      // Update user's profile completion status
      existingUser.isProfileCompleted = true;
      await existingUser.save();
      console.log(
        "✅ PERSONAL PROFILE MARKED AS COMPLETED:",
        existingUser.email,
      );

      logger.info(`Personal profile created for user: ${authReq.user.email}`);

      res.status(201).json({
        success: true,
        message: "Personal profile created successfully",
        data: {
          user: {
            id: existingUser._id,
            email: existingUser.email,
            role: existingUser.role,
            isProfileCompleted: existingUser.isProfileCompleted,
            status: existingUser.status,
          },
          profile: {
            id: personal._id,
            userId: personal.userId,
            fullName: personal.fullName,
            gender: personal.gender,
            dateOfBirth: personal.dateOfBirth,
            phone: personal.phone,
            address: personal.address,
            bio: personal.bio,
            profileImage: personal.profileImage,
            createdAt: personal.createdAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Personal profile creation error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Create school profile
   */
  async createSchoolProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      console.log("🔍 USER ID:", userId);
      console.log("🔍 USER ROLE:", userRole);

      // Only school users can create school profiles
      if (userRole !== "school") {
        res.status(403).json({
          success: false,
          message: "Only school users can create school profiles",
        } as APIResponse);
        return;
      }

      // Check if user already has a school profile
      const existingSchool = await School.findOne({
        userId: Types.ObjectId.createFromHexString(userId),
      });
      console.log(
        "🔍 EXISTING SCHOOL:",
        existingSchool ? "FOUND" : "NOT FOUND",
      );

      // Debug: Check all school profiles for this user
      const allSchoolsForUser = await School.find({
        userId: Types.ObjectId.createFromHexString(userId),
      });
      console.log("🔍 ALL SCHOOLS FOR USER:", allSchoolsForUser.length);

      if (existingSchool) {
        res.status(409).json({
          success: false,
          message: "School profile already exists",
          data: {
            existingSchool: {
              id: existingSchool._id,
              schoolName: existingSchool.schoolName,
              status: existingSchool.status,
            },
          },
        } as APIResponse);
        return;
      }

      // Parse form data for complex fields
      const formData = { ...req.body };

      // Parse address if it's a JSON string
      if (formData.address && typeof formData.address === "string") {
        try {
          formData.address = JSON.parse(formData.address);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      // Parse primaryContact if it's a JSON string
      if (
        formData.primaryContact &&
        typeof formData.primaryContact === "string"
      ) {
        try {
          formData.primaryContact = JSON.parse(formData.primaryContact);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      // Parse date of birth in primaryContact if provided
      if (formData.primaryContact?.dateOfBirth) {
        formData.primaryContact.dateOfBirth = new Date(
          formData.primaryContact.dateOfBirth,
        );
      }

      console.log("🔍 ABOUT TO VALIDATE SCHOOL PROFILE:");
      console.log(
        "formData before validation:",
        JSON.stringify(formData, null, 2),
      );

      // Validate request body BEFORE converting address
      const { error, value } = schoolProfileValidation.validate(formData, {
        allowUnknown: true, // Allow extra fields
        stripUnknown: true, // Remove unknown fields
      });
      if (error) {
        console.log(
          "❌ SCHOOL PROFILE VALIDATION ERROR:",
          error.details[0].message,
        );
        console.log("Full error details:", error.details);
        res.status(400).json({
          success: false,
          message: "School profile validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      // Convert flat address fields to nested address object for database storage
      // Only if address is NOT already an object
      if (
        typeof value.address === "string" ||
        value.city ||
        value.state ||
        value.country ||
        value.postalCode
      ) {
        // If address is already a nested object, don't convert it
        if (typeof value.address === "object" && value.address !== null) {
          // Address is already an object, just remove flat fields if they exist
          delete value.city;
          delete value.state;
          delete value.country;
          delete value.postalCode;
        } else {
          // Convert flat fields to nested object
          const addressObj = {
            street: value.address || "",
            city: value.city || "",
            state: value.state || "",
            country: value.country || "",
            postalCode: value.postalCode || "",
          };
          value.address = addressObj;

          // Remove the flat fields after converting to nested object
          delete value.city;
          delete value.state;
          delete value.country;
          delete value.postalCode;
        }
      }

      // Process uploaded files
      const fileData = processUploadedFiles((req as any).files);

      // Create school profile
      const school = new School({
        userId,
        ...value,
        ...fileData,
        // Fix: Auto-approval will be handled by model middleware
      });

      await school.save();

      // Mark user's profile as completed
      const user = await User.findById(userId);
      if (user) {
        user.isProfileCompleted = true;
        await user.save();
        console.log("✅ SCHOOL USER PROFILE MARKED AS COMPLETED:", user.email);
      }

      logger.info(`School profile created for user: ${authReq.user.email}`);

      res.status(201).json({
        success: true,
        message: "School profile created successfully",
        data: {
          school: {
            id: school._id,
            userId: school.userId,
            schoolName: school.schoolName,
            establishmentYear: school.establishmentYear,
            phone: school.phone,
            website: school.website,
            email: school.email,
            address: school.address,
            primaryContact: school.primaryContact
              ? {
                  ...school.primaryContact,
                  age: school.get("primaryContact.age"), // Include calculated age
                }
              : undefined,
            description: school.description,
            logo: school.logo,
            documents: school.documents,
            status: school.status,
            isVerified: school.isVerified,
            createdAt: school.createdAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("School profile creation error:", error);

      if (error instanceof mongoose.Error.ValidationError) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.message,
        } as APIResponse);
      } else if ((error as any).code === 11_000) {
        console.error("❌ DUPLICATE KEY ERROR!");
        console.error("Error details:", error);

        // Get userId from authReq for catch block
        const catchUserId = (req as AuthenticatedRequest).user?.userId;

        // Try to find the existing school profile
        const existingSchool = await School.findOne({
          userId: catchUserId
            ? Types.ObjectId.createFromHexString(catchUserId)
            : undefined,
        });

        res.status(409).json({
          success: false,
          message: "A school profile already exists for this user",
          error: "Duplicate school profile",
          data: existingSchool
            ? {
                existingSchool: {
                  id: existingSchool._id,
                  schoolName: existingSchool.schoolName,
                  status: existingSchool.status,
                },
              }
            : null,
        } as APIResponse);
      } else {
        res.status(500).json({
          success: false,
          message: "Internal server error. Please try again later.",
        } as APIResponse);
      }
    }
  }

  /**
   * Create student profile
   */
  async createStudentProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only student users can create student profiles
      if (userRole !== "student") {
        res.status(403).json({
          success: false,
          message: "Only student users can create student profiles",
        } as APIResponse);
        return;
      }

      // Check if user already has a student profile
      const existingStudent = await Student.findOne({ userId });
      if (existingStudent) {
        res.status(409).json({
          success: false,
          message: "Student profile already exists",
        } as APIResponse);
        return;
      }

      // Parse form data for complex fields
      const formData = { ...req.body };

      // Parse address if it's a JSON string
      if (formData.address && typeof formData.address === "string") {
        try {
          formData.address = JSON.parse(formData.address);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      // Parse arrays if they're JSON strings
      if (
        formData.majorSubjectsIds &&
        typeof formData.majorSubjectsIds === "string"
      ) {
        try {
          formData.majorSubjectsIds = JSON.parse(formData.majorSubjectsIds);
        } catch {
          // Keep as string if not valid JSON
        }
      }
      if (
        formData.majorCourseIds &&
        typeof formData.majorCourseIds === "string"
      ) {
        try {
          formData.majorCourseIds = JSON.parse(formData.majorCourseIds);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      if (
        formData.minorSubjects &&
        typeof formData.minorSubjects === "string"
      ) {
        try {
          formData.minorSubjects = JSON.parse(formData.minorSubjects);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      if (formData.guardians && typeof formData.guardians === "string") {
        try {
          formData.guardians = JSON.parse(formData.guardians);
        } catch (error) {
          // Keep as string if not valid JSON
          console.log(
            "Failed to parse guardians:",
            formData.guardians,
            "\n",
            error,
          );
        }
      }

      // Parse date of birth
      if (formData.dateOfBirth) {
        formData.dateOfBirth = new Date(formData.dateOfBirth);
      }

      // DEBUG: Log what we received for student profile
      console.log("🎓 STUDENT PROFILE DEBUG:");
      console.log("req.body:", req.body);
      console.log("req.files:", (req as any).files);
      console.log("formData after processing:", formData);

      // Validate request body
      const { error, value } = studentProfileValidation.validate(formData, {
        allowUnknown: true, // Allow extra fields
        stripUnknown: true, // Remove unknown fields
      });
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      applyPhoneNumberAlias(value as Record<string, unknown>);

      // Handle age to dateOfBirth conversion for students
      if (value.age && !value.dateOfBirth) {
        const currentYear = new Date().getFullYear();
        const birthYear = currentYear - Number.parseInt(value.age);
        value.dateOfBirth = new Date(birthYear, 0, 1); // January 1st of birth year
        delete value.age; // Remove the age field after conversion
      }

      // Convert flat address fields to nested address object for database storage
      if (
        value.address ||
        value.city ||
        value.state ||
        value.country ||
        value.postalCode
      ) {
        const addressObj = {
          street: value.address || "",
          city: value.city || "",
          state: value.state || "",
          country: value.country || "",
          postalCode: value.postalCode || "",
        };
        value.address = addressObj;

        // Remove the flat fields after converting to nested object
        delete value.city;
        delete value.state;
        delete value.country;
        delete value.postalCode;
      }

      // Convert majorSubjectsIds strings to ObjectIds (deprecated)
      if (value.majorSubjectsIds && Array.isArray(value.majorSubjectsIds)) {
        value.majorSubjectsIds = value.majorSubjectsIds
          .filter((id: string) => mongoose.Types.ObjectId.isValid(id))
          .map((id: string) => new mongoose.Types.ObjectId(id));
      }
      // Convert majorCourseIds strings to ObjectIds (preferred - Course IDs)
      if (value.majorCourseIds && Array.isArray(value.majorCourseIds)) {
        value.majorCourseIds = value.majorCourseIds
          .filter((id: string) => mongoose.Types.ObjectId.isValid(id))
          .map((id: string) => new mongoose.Types.ObjectId(id));
      }

      // Verify school exists and is active
      const school = await School.findOne({
        _id: value.schoolId,
        status: "approved",
        isVerified: true,
      });
      if (!school) {
        res.status(400).json({
          success: false,
          message: "Invalid or inactive school ID",
        } as APIResponse);
        return;
      }

      // Process uploaded files (profile image + documents)
      const fileData = processUploadedFiles((req as any).files);

      // Process documents with their types
      let documentTypes = [];
      if (formData.documentTypes) {
        documentTypes =
          typeof formData.documentTypes === "string"
            ? JSON.parse(formData.documentTypes)
            : formData.documentTypes;
      }

      if (fileData.documents && Array.isArray(fileData.documents)) {
        fileData.documents = fileData.documents.map(
          (doc: any, index: number) => ({
            ...doc,
            documentType: documentTypes?.[index] || "other", // Assign document type or default to "other"
            verified: false, // Default verification status
          }),
        );
      }

      // Clean up fileData to remove incomplete objects
      if (fileData.profilePicture && !fileData.profilePicture.fileName) {
        delete fileData.profilePicture;
      }

      // Also remove profilePicture passed in body if it's an empty object
      if (value.profilePicture && !value.profilePicture.fileName) {
        delete value.profilePicture;
      }

      // Create student profile
      const student = new Student({
        userId,
        schoolId: school._id,
        ...value,
        ...fileData,
        // Fix: Auto-approval will be handled by model middleware
      });

      await student.save();

      // Populate school details after saving
      await student.populate(
        "schoolId",
        "schoolName establishmentYear phone website email address description logo status isVerified",
      );

      // Mark user's profile as completed
      const user = await User.findById(userId);
      if (user) {
        user.isProfileCompleted = true;
        await user.save();
        console.log("✅ STUDENT USER PROFILE MARKED AS COMPLETED:", user.email);
      }

      // Notify school about student join request
      // eslint-disable-next-line node/no-unsupported-features/es-syntax
      const { notificationService } = await import(
        "../services/notification-service"
      );
      await notificationService.notifyStudentJoinRequest(
        school._id,
        student._id,
        student.fullName,
        user?.email || "",
      );

      logger.info(`Student profile created for user: ${authReq.user.email}`);

      res.status(201).json({
        success: true,
        message: "Student profile created successfully",
        data: {
          student: {
            id: student._id,
            userId: student.userId,
            schoolId: student.schoolId,
            school: student.schoolId, // Populated school details
            studentId: student.studentId,
            fullName: student.fullName,
            dateOfBirth: student.dateOfBirth,
            gender: student.gender,
            phone: student.phone,
            address: student.address,
            profilePicture: student.profilePicture,
            documents: student.documents,
            academicLevel: student.academicLevel,
            academicYear: student.academicYear,
            majorSubjectsIds: student.majorSubjectsIds,
            majorCourseIds: student.majorCourseIds,
            minorSubjects: student.minorSubjects,
            guardians: student.guardians,
            isApproved: student.isApproved,
            status: student.status,
            createdAt: student.createdAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Student profile creation error:", error);

      if (error instanceof mongoose.Error.ValidationError) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.message,
        } as APIResponse);
      } else {
        res.status(500).json({
          success: false,
          message: "Internal server error. Please try again later.",
        } as APIResponse);
      }
    }
  }

  /**
   * Create teacher profile
   */
  async createTeacherProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // DEBUG: Log what we received for teacher profile
      console.log("👨‍🏫 TEACHER PROFILE DEBUG:");
      console.log("req.body:", req.body);
      console.log("req.files:", (req as any).files);
      console.log("req.file:", (req as any).file);

      // Only teacher users can create teacher profiles
      if (userRole !== "teacher") {
        res.status(403).json({
          success: false,
          message: "Only teacher users can create teacher profiles",
        } as APIResponse);
        return;
      }

      // Check if user already has a teacher profile
      const existingTeacher = await Teacher.findOne({ userId });
      if (existingTeacher) {
        res.status(409).json({
          success: false,
          message: "Teacher profile already exists",
        } as APIResponse);
        return;
      }

      // Parse form data for complex fields
      const formData = { ...req.body };

      // Parse address if it's a JSON string
      if (formData.address && typeof formData.address === "string") {
        try {
          formData.address = JSON.parse(formData.address);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      // Parse arrays and objects if they're JSON strings
      const jsonFields = [
        "subjects",
        "majorSubjects",
        "courseIds",
        "education",
        "experience",
        "emergencyContact",
      ];
      for (const field of jsonFields) {
        if (formData[field] && typeof formData[field] === "string") {
          try {
            formData[field] = JSON.parse(formData[field]);
          } catch {
            // Keep as string if not valid JSON
          }
        }
      }

      // Transform majorSubjects to subjects format if provided (deprecated)
      if (formData.majorSubjects && Array.isArray(formData.majorSubjects)) {
        formData.subjects = formData.majorSubjects.map((subject: string) => ({
          name: subject,
        }));
        delete formData.majorSubjects;
      }

      // Handle age to dateOfBirth conversion
      if (formData.age && !formData.dateOfBirth) {
        const currentYear = new Date().getFullYear();
        const birthYear = currentYear - Number.parseInt(formData.age);
        formData.dateOfBirth = new Date(birthYear, 0, 1); // January 1st of birth year
        delete formData.age; // Remove the age field after conversion
      }

      // Parse date of birth before validation
      if (formData.dateOfBirth) {
        formData.dateOfBirth = new Date(formData.dateOfBirth);
      }

      // DEBUG: Log formData before validation
      console.log("📝 TEACHER VALIDATION DEBUG:");
      console.log(
        "formData before validation:",
        JSON.stringify(formData, null, 2),
      );

      // Validate request body FIRST
      const { error, value } = teacherProfileValidation.validate(formData, {
        allowUnknown: true,
        stripUnknown: true,
      });
      if (error) {
        console.log("❌ VALIDATION ERROR:", error.details[0].message);
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      applyPhoneNumberAlias(value as Record<string, unknown>);

      // Now process complex fields AFTER validation
      // Convert flat address fields to nested address object for database storage
      if (
        value.address ||
        value.city ||
        value.state ||
        value.country ||
        value.postalCode
      ) {
        const addressObj = {
          street: value.address || "",
          city: value.city || "",
          state: value.state || "",
          country: value.country || "",
          postalCode: value.postalCode || "",
        };
        value.address = addressObj;

        // Remove the flat fields after converting to nested object
        delete value.city;
        delete value.state;
        delete value.country;
        delete value.postalCode;
      }

      // Parse arrays and objects if they're JSON strings (after validation)
      const jsonFieldsAfterValidation = [
        "subjects",
        "education",
        "experience",
        "emergencyContact",
      ];
      for (const field of jsonFieldsAfterValidation) {
        if (value[field] && typeof value[field] === "string") {
          try {
            value[field] = JSON.parse(value[field]);
          } catch {
            // Keep as string if not valid JSON
          }
        }
      }

      // Parse dates in experience array
      if (value.experience && Array.isArray(value.experience)) {
        value.experience = value.experience.map((exp: any) => ({
          ...exp,
          startDate: exp.startDate ? new Date(exp.startDate) : undefined,
          endDate: exp.endDate ? new Date(exp.endDate) : undefined,
        }));
      }

      // Convert courseIds strings to ObjectIds if provided
      if (value.courseIds && Array.isArray(value.courseIds)) {
        value.courseIds = value.courseIds
          .filter((id: string) => mongoose.Types.ObjectId.isValid(id))
          .map((id: string) => new mongoose.Types.ObjectId(id));
      }

      // Verify school exists and is active
      const school = await School.findOne({
        _id: value.schoolId,
        status: "approved",
        isVerified: true,
      });
      if (!school) {
        res.status(400).json({
          success: false,
          message: "Invalid or inactive school ID",
        } as APIResponse);
        return;
      }

      // Process document types if provided
      let documentTypes: string[] | undefined;
      if (formData.documentTypes) {
        try {
          documentTypes =
            typeof formData.documentTypes === "string"
              ? JSON.parse(formData.documentTypes)
              : formData.documentTypes;
        } catch {
          // If parsing fails, treat as single value or ignore
          documentTypes = Array.isArray(formData.documentTypes)
            ? formData.documentTypes
            : [formData.documentTypes];
        }
      }

      // Process uploaded files (profile image + documents)
      const fileData = processUploadedFiles((req as any).files);

      // Process documents with their types
      if (fileData.documents && Array.isArray(fileData.documents)) {
        fileData.documents = fileData.documents.map(
          (doc: any, index: number) => ({
            ...doc,
            documentType: documentTypes?.[index] || "other", // Assign document type or default to "other"
            verified: false, // Default verification status
          }),
        );
      }

      // Clean up fileData to remove incomplete objects
      // if (fileData.profilePicture && !fileData.profilePicture.fileName) {
      //   delete fileData.profilePicture;
      // }

      // Create teacher profile
      const teacher = new Teacher({
        userId,
        schoolId: school._id,
        ...value,
        ...fileData,
        // Fix: Auto-approval will be handled by model middleware
      });

      await teacher.save();

      // Populate school details after saving
      await teacher.populate(
        "schoolId",
        "schoolName establishmentYear phone website email address description logo status isVerified",
      );

      // Mark user's profile as completed
      const user = await User.findById(userId);
      if (user) {
        user.isProfileCompleted = true;
        await user.save();
        console.log("✅ TEACHER USER PROFILE MARKED AS COMPLETED:", user.email);
      }

      // Notify school about teacher join request
      // eslint-disable-next-line node/no-unsupported-features/es-syntax
      const { notificationService } = await import(
        "../services/notification-service"
      );
      await notificationService.notifyTeacherJoinRequest(
        school._id,
        teacher._id,
        teacher.fullName,
        user?.email || "",
      );

      logger.info(`Teacher profile created for user: ${authReq.user.email}`);

      res.status(201).json({
        success: true,
        message: "Teacher profile created successfully",
        data: {
          teacher: {
            id: teacher._id,
            userId: teacher.userId,
            schoolId: teacher.schoolId,
            employeeId: teacher.employeeId,
            fullName: teacher.fullName,
            dateOfBirth: teacher.dateOfBirth,
            gender: teacher.gender,
            phone: teacher.phone,
            address: teacher.address,
            profilePicture: teacher.profilePicture,
            bio: teacher.bio,
            subjects: teacher.subjects,
            courseIds: teacher.courseIds,
            education: teacher.education,
            experience: teacher.experience,
            documents: teacher.documents,
            emergencyContact: teacher.emergencyContact,
            isApproved: teacher.isApproved,
            status: teacher.status,
            createdAt: teacher.createdAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher profile creation error:", error);

      if (error instanceof mongoose.Error.ValidationError) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.message,
        } as APIResponse);
      } else if (
        (error as any).isJoi ||
        (error as any).name === "ValidationError"
      ) {
        // Handle Joi validation errors
        res.status(400).json({
          success: false,
          message: "Input validation failed",
          error: (error as any).details
            ? (error as any).details[0].message
            : String((error as any).message || error),
        } as APIResponse);
      } else {
        res.status(500).json({
          success: false,
          message: "Internal server error. Please try again later.",
        } as APIResponse);
      }
    }
  }

  /**
   * Get user's own profile
   */
  async getMyProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Find user
      const user = await User.findById(userId);
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      let profile = null;
      let personalProfile = null;

      // Get role-specific profile
      switch (userRole) {
        case "school": {
          profile = await School.findOne({ userId });
          personalProfile = await Personal.findOne({ userId });
          break;
        }
        case "student": {
          profile = await Student.findOne({ userId })
            .populate(
              "schoolId",
              "schoolName schoolCode establishmentYear phone website email address description logo status isVerified",
            )
            .populate(
              "majorSubjectsIds",
              "className classCode courseName courseCode",
            )
            .populate("majorCourseIds", "courseCode courseName");
          // Add majorSubjects with id, code, name for display (prefer majorCourseIds)
          if (profile) {
            const coursePopulated =
              (profile.majorCourseIds as any[])?.filter(Boolean) || [];
            const classPopulated =
              (profile.majorSubjectsIds as any[])?.filter(Boolean) || [];
            const subjects =
              coursePopulated.length > 0
                ? coursePopulated.map((c: any) => ({
                    id: String(c._id),
                    name: c.courseName || c.courseCode || "",
                    code: c.courseCode || "",
                  }))
                : classPopulated.map((cls: any) => ({
                    id: String(cls._id),
                    name: cls.courseName || cls.className || "",
                    code: cls.courseCode || cls.classCode || "",
                  }));
            (profile as any).majorSubjects = subjects;
          }
          break;
        }
        case "teacher": {
          profile = await Teacher.findOne({ userId })
            .populate(
              "schoolId",
              "schoolName schoolCode establishmentYear phone website email address description logo status isVerified",
            )
            .populate("courseIds", "courseCode courseName");
          if (profile) {
            const courses = (
              (profile as { courseIds?: unknown[] }).courseIds || []
            ).filter(Boolean);
            (profile as { majorCourseIds?: unknown }).majorCourseIds =
              courses.map((c: unknown) => {
                const doc = c as {
                  _id?: mongoose.Types.ObjectId;
                  courseCode?: string;
                  courseName?: string;
                };
                return {
                  id: String(doc._id ?? c),
                  courseCode: doc.courseCode ?? "",
                  courseName: doc.courseName ?? "",
                };
              });
          }
          break;
        }
        default: {
          // Admin users don't have specific profiles
          break;
        }
      }

      const phone =
        profile && (userRole === "student" || userRole === "teacher")
          ? (profile as any).phone
          : undefined;

      const schoolCode =
        profile &&
        (userRole === "student" || userRole === "teacher") &&
        (profile as any).schoolId
          ? (profile as any).schoolId.schoolCode
          : undefined;

      // Student: canonical approval for API is approvalStatus ("pending" vs "approved").
      let profilePayload: unknown = profile;
      if (
        userRole === "student" &&
        profile != null &&
        typeof profile === "object"
      ) {
        const doc = profile as mongoose.Document & { approvalStatus?: string };
        const plain =
          typeof doc.toObject === "function"
            ? doc.toObject({ virtuals: true })
            : { ...(profile as unknown as Record<string, unknown>) };
        (plain as { isApproved: boolean }).isApproved =
          plain.approvalStatus === PROFILE_APPROVAL_STATUS.APPROVED;
        profilePayload = plain;
      }

      res.status(200).json({
        success: true,
        message: "Profile retrieved successfully",
        data: {
          user: {
            id: user._id,
            email: user.email,
            phone: phone || null,
            role: user.role,
            status: user.status,
            isEmailVerified: user.isEmailVerified,
            isProfileCompleted: user.isProfileCompleted,
            schoolCode: schoolCode || null,
            createdAt: user.createdAt,
          },
          profile: profilePayload || null,
          personalProfile: personalProfile || null,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get profile error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get personal profile
   */
  async getPersonalProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only school users can access personal profiles
      if (userRole !== "school") {
        res.status(403).json({
          success: false,
          message: "Only school users can access personal profiles",
        } as APIResponse);
        return;
      }

      const personalProfile = await Personal.findOne({ userId });
      if (!personalProfile) {
        res.status(404).json({
          success: false,
          message: "Personal profile not found",
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: "Personal profile retrieved successfully",
        data: {
          profile: personalProfile,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get personal profile error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Update personal profile
   */
  async updatePersonalProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only school users can update personal profiles
      if (userRole !== "school") {
        res.status(403).json({
          success: false,
          message: "Only school users can update personal profiles",
        } as APIResponse);
        return;
      }

      const existingPersonal = await Personal.findOne({ userId });
      if (!existingPersonal) {
        res.status(404).json({
          success: false,
          message: "Personal profile not found. Please create a profile first.",
        } as APIResponse);
        return;
      }

      // Parse form data for complex fields
      const formData = { ...req.body };

      // Parse address if it's a JSON string
      if (formData.address && typeof formData.address === "string") {
        try {
          formData.address = JSON.parse(formData.address);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      // Parse date of birth
      if (formData.dateOfBirth) {
        formData.dateOfBirth = new Date(formData.dateOfBirth);
      }

      // Validate request body
      const { error, value } =
        updatePersonalProfileValidation.validate(formData);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      // Process uploaded files if any
      const fileData = processUploadedFiles(
        (req as any).files || { profileImage: [(req as any).file] },
      );

      // Update personal profile
      Object.assign(existingPersonal, value, fileData);
      await existingPersonal.save();

      logger.info(`Personal profile updated for user: ${authReq.user.email}`);

      res.status(200).json({
        success: true,
        message: "Personal profile updated successfully",
        data: {
          profile: {
            id: existingPersonal._id,
            userId: existingPersonal.userId,
            fullName: existingPersonal.fullName,
            gender: existingPersonal.gender,
            dateOfBirth: existingPersonal.dateOfBirth,
            phone: existingPersonal.phone,
            address: existingPersonal.address,
            bio: existingPersonal.bio,
            profileImage: existingPersonal.profileImage,
            updatedAt: existingPersonal.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Personal profile update error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get school profile
   */
  async getSchoolProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only school users can access school profiles
      if (userRole !== "school") {
        res.status(403).json({
          success: false,
          message: "Only school users can access school profiles",
        } as APIResponse);
        return;
      }

      const schoolProfile = await School.findOne({ userId });
      if (!schoolProfile) {
        res.status(404).json({
          success: false,
          message: "School profile not found",
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: "School profile retrieved successfully",
        data: {
          profile: schoolProfile,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get school profile error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Update school profile
   */
  async updateSchoolProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only school users can update school profiles
      if (userRole !== "school") {
        res.status(403).json({
          success: false,
          message: "Only school users can update school profiles",
        } as APIResponse);
        return;
      }

      const existingSchool = await School.findOne({ userId });
      if (!existingSchool) {
        res.status(404).json({
          success: false,
          message: "School profile not found. Please create a profile first.",
        } as APIResponse);
        return;
      }

      // Parse form data for complex fields
      const formData = { ...req.body };

      // Parse address if it's a JSON string
      if (formData.address && typeof formData.address === "string") {
        try {
          formData.address = JSON.parse(formData.address);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      // Parse primaryContact if it's a JSON string
      if (
        formData.primaryContact &&
        typeof formData.primaryContact === "string"
      ) {
        try {
          formData.primaryContact = JSON.parse(formData.primaryContact);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      // Parse date of birth in primaryContact if provided
      if (formData.primaryContact?.dateOfBirth) {
        formData.primaryContact.dateOfBirth = new Date(
          formData.primaryContact.dateOfBirth,
        );
      }

      // Create validation schema for update (making all fields optional)
      const updateSchoolValidation = schoolProfileValidation.fork(
        Object.keys(schoolProfileValidation.describe().keys),
        schema => schema.optional(),
      );

      // Validate request body
      const { error, value } = updateSchoolValidation.validate(formData);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      // Convert flat address fields to nested address object (if needed)
      if (
        typeof value.address === "string" ||
        value.city ||
        value.state ||
        value.country ||
        value.postalCode
      ) {
        // If address is already a nested object, don't convert it
        if (typeof value.address === "object" && value.address !== null) {
          // Address is already an object, just remove flat fields if they exist
          delete value.city;
          delete value.state;
          delete value.country;
          delete value.postalCode;
        } else {
          // Convert flat fields to nested object
          const addressObj = {
            street: value.address || "",
            city: value.city || "",
            state: value.state || "",
            country: value.country || "",
            postalCode: value.postalCode || "",
          };
          value.address = addressObj;

          // Remove the flat fields after converting to nested object
          delete value.city;
          delete value.state;
          delete value.country;
          delete value.postalCode;
        }
      }

      // Handle removeDocuments array if provided
      if (formData.removeDocuments) {
        let documentsToRemove = [];
        try {
          documentsToRemove =
            typeof formData.removeDocuments === "string"
              ? JSON.parse(formData.removeDocuments)
              : formData.removeDocuments;
        } catch {
          res.status(400).json({
            success: false,
            message:
              "Invalid removeDocuments format. Should be an array of document filenames.",
          } as APIResponse);
          return;
        }

        if (Array.isArray(documentsToRemove) && documentsToRemove.length > 0) {
          // Remove specified documents
          existingSchool.documents = (existingSchool.documents || []).filter(
            (doc: any) => !documentsToRemove.includes(doc.fileName),
          );
          logger.info(
            `${documentsToRemove.length} documents marked for removal for user: ${authReq.user.email}`,
          );
        }
      }

      // Process uploaded files if any
      const fileData = processUploadedFiles((req as any).files);

      // Add new documents to existing ones (after removal)
      if (fileData.documents && Array.isArray(fileData.documents)) {
        const existingDocuments = existingSchool.documents || [];
        fileData.documents = [...existingDocuments, ...fileData.documents];

        // Limit total documents to prevent abuse
        const MAX_DOCUMENTS = 20;
        if (fileData.documents.length > MAX_DOCUMENTS) {
          res.status(400).json({
            success: false,
            message: `Maximum ${MAX_DOCUMENTS} documents allowed. You currently have ${existingDocuments.length} documents. You can add ${MAX_DOCUMENTS - existingDocuments.length} more.`,
          } as APIResponse);
          return;
        }
      }

      // Update school profile
      const updateData = value;
      Object.assign(existingSchool, updateData, fileData);
      await existingSchool.save();

      logger.info(`School profile updated for user: ${authReq.user.email}`);

      res.status(200).json({
        success: true,
        message: "School profile updated successfully",
        data: {
          school: {
            id: existingSchool._id,
            userId: existingSchool.userId,
            schoolName: existingSchool.schoolName,
            establishmentYear: existingSchool.establishmentYear,
            phone: existingSchool.phone,
            website: existingSchool.website,
            email: existingSchool.email,
            address: existingSchool.address,
            primaryContact: existingSchool.primaryContact
              ? {
                  ...(existingSchool.primaryContact as any),
                  age: existingSchool.get("primaryContact.age"), // Include calculated age
                }
              : undefined,
            description: existingSchool.description,
            logo: existingSchool.logo,
            documents: existingSchool.documents,
            status: existingSchool.status,
            isVerified: existingSchool.isVerified,
            updatedAt: existingSchool.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("School profile update error:", error);

      if (error instanceof mongoose.Error.ValidationError) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.message,
        } as APIResponse);
      } else {
        res.status(500).json({
          success: false,
          message: "Internal server error. Please try again later.",
        } as APIResponse);
      }
    }
  }

  /**
   * Update teacher profile
   */
  async updateTeacherProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only teacher users can update teacher profiles
      if (userRole !== "teacher") {
        res.status(403).json({
          success: false,
          message: "Only teacher users can update teacher profiles",
        } as APIResponse);
        return;
      }

      const existingTeacher = await Teacher.findOne({ userId });
      if (!existingTeacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found. Please create a profile first.",
        } as APIResponse);
        return;
      }

      // Parse form data for complex fields
      const formData = { ...req.body };

      // Parse address if it's a JSON string
      if (formData.address && typeof formData.address === "string") {
        try {
          formData.address = JSON.parse(formData.address);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      // Parse arrays and objects if they're JSON strings
      const jsonFields = [
        "subjects",
        "majorSubjects",
        "courseIds",
        "education",
        "experience",
        "emergencyContact",
      ];
      for (const field of jsonFields) {
        if (formData[field] && typeof formData[field] === "string") {
          try {
            formData[field] = JSON.parse(formData[field]);
          } catch {
            // Keep as string if not valid JSON
          }
        }
      }

      // Transform majorSubjects to subjects format if provided
      if (formData.majorSubjects && Array.isArray(formData.majorSubjects)) {
        formData.subjects = formData.majorSubjects.map((subject: string) => ({
          name: subject,
        }));
        delete formData.majorSubjects;
      }

      // Handle age to dateOfBirth conversion
      if (formData.age && !formData.dateOfBirth) {
        const currentYear = new Date().getFullYear();
        const birthYear = currentYear - Number.parseInt(formData.age);
        formData.dateOfBirth = new Date(birthYear, 0, 1);
        delete formData.age;
      }

      // Parse date of birth
      if (formData.dateOfBirth) {
        formData.dateOfBirth = new Date(formData.dateOfBirth);
      }

      // Create validation schema for update (making all fields optional)
      const updateTeacherValidation = teacherProfileValidation.fork(
        Object.keys(teacherProfileValidation.describe().keys),
        schema => schema.optional(),
      );

      // Validate request body
      const { error, value } = updateTeacherValidation.validate(formData);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      applyPhoneNumberAlias(value as Record<string, unknown>);

      // Convert flat address fields to nested address object
      if (
        value.address ||
        value.city ||
        value.state ||
        value.country ||
        value.postalCode
      ) {
        const addressObj = {
          street: value.address || "",
          city: value.city || "",
          state: value.state || "",
          country: value.country || "",
          postalCode: value.postalCode || "",
        };
        value.address = addressObj;

        // Remove the flat fields after converting to nested object
        delete value.city;
        delete value.state;
        delete value.country;
        delete value.postalCode;
      }

      // Parse dates in experience array
      if (value.experience && Array.isArray(value.experience)) {
        value.experience = value.experience.map((exp: any) => ({
          ...exp,
          startDate: exp.startDate ? new Date(exp.startDate) : undefined,
          endDate: exp.endDate ? new Date(exp.endDate) : undefined,
        }));
      }

      // Process uploaded files if any
      const fileData = processUploadedFiles((req as any).files);

      // Process documents with their types
      if (fileData.documents && Array.isArray(fileData.documents)) {
        let documentTypes = [];
        if (formData.documentTypes) {
          documentTypes =
            typeof formData.documentTypes === "string"
              ? JSON.parse(formData.documentTypes)
              : formData.documentTypes;
        }

        fileData.documents = fileData.documents.map(
          (doc: any, index: number) => ({
            ...doc,
            documentType: documentTypes[index] || "other",
            verified: false,
          }),
        );
      }

      // Clean up fileData to remove incomplete objects
      if (fileData.profilePicture && !fileData.profilePicture.fileName) {
        delete fileData.profilePicture;
      }

      // Also remove profilePicture passed in body if it's an empty object
      if (value.profilePicture && !value.profilePicture.fileName) {
        delete value.profilePicture;
      }

      // Convert courseIds strings to ObjectIds if provided
      if (value.courseIds && Array.isArray(value.courseIds)) {
        value.courseIds = value.courseIds
          .filter((id: string) => mongoose.Types.ObjectId.isValid(id))
          .map((id: string) => new mongoose.Types.ObjectId(id));
      }

      // Update teacher profile (excluding schoolId as it should be immutable)
      const { schoolId, ...updateData } = value;
      Object.assign(existingTeacher, updateData, fileData);
      await existingTeacher.save();

      // Populate school details after saving
      await existingTeacher.populate(
        "schoolId",
        "schoolName establishmentYear phone website email address description logo status isVerified",
      );

      logger.info(`Teacher profile updated for user: ${authReq.user.email}`);

      res.status(200).json({
        success: true,
        message: "Teacher profile updated successfully",
        data: {
          teacher: {
            id: existingTeacher._id,
            userId: existingTeacher.userId,
            schoolId: existingTeacher.schoolId,
            employeeId: existingTeacher.employeeId,
            fullName: existingTeacher.fullName,
            dateOfBirth: existingTeacher.dateOfBirth,
            gender: existingTeacher.gender,
            phone: existingTeacher.phone,
            address: existingTeacher.address,
            profilePicture: existingTeacher.profilePicture,
            bio: existingTeacher.bio,
            subjects: existingTeacher.subjects,
            courseIds: existingTeacher.courseIds,
            education: existingTeacher.education,
            experience: existingTeacher.experience,
            documents: existingTeacher.documents,
            emergencyContact: existingTeacher.emergencyContact,
            isApproved: existingTeacher.isApproved,
            status: existingTeacher.status,
            updatedAt: existingTeacher.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher profile update error:", error);

      if (error instanceof mongoose.Error.ValidationError) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.message,
        } as APIResponse);
      } else {
        res.status(500).json({
          success: false,
          message: "Internal server error. Please try again later.",
        } as APIResponse);
      }
    }
  }

  /**
   * Update student profile
   */
  async updateStudentProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only student users can update student profiles
      if (userRole !== "student") {
        res.status(403).json({
          success: false,
          message: "Only student users can update student profiles",
        } as APIResponse);
        return;
      }

      const existingStudent = await Student.findOne({ userId });
      if (!existingStudent) {
        res.status(404).json({
          success: false,
          message: "Student profile not found. Please create a profile first.",
        } as APIResponse);
        return;
      }

      // Parse form data for complex fields
      const formData = { ...req.body };

      // Parse address if it's a JSON string
      if (formData.address && typeof formData.address === "string") {
        try {
          formData.address = JSON.parse(formData.address);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      // Parse arrays if they're JSON strings
      const arrayFields = [
        "majorSubjectsIds",
        "majorCourseIds",
        "minorSubjects",
        "guardians",
      ];
      for (const field of arrayFields) {
        if (formData[field] && typeof formData[field] === "string") {
          try {
            formData[field] = JSON.parse(formData[field]);
          } catch {
            // Keep as string if not valid JSON
          }
        }
      }

      // Parse date of birth
      if (formData.dateOfBirth) {
        formData.dateOfBirth = new Date(formData.dateOfBirth);
      }

      // Handle age to dateOfBirth conversion
      if (formData.age && !formData.dateOfBirth) {
        const currentYear = new Date().getFullYear();
        const birthYear = currentYear - Number.parseInt(formData.age);
        formData.dateOfBirth = new Date(birthYear, 0, 1);
        delete formData.age;
      }

      // Create validation schema for update (making all fields optional)
      const updateStudentValidation = studentProfileValidation.fork(
        Object.keys(studentProfileValidation.describe().keys),
        schema => schema.optional(),
      );

      // Validate request body
      const { error, value } = updateStudentValidation.validate(formData);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      applyPhoneNumberAlias(value as Record<string, unknown>);

      // Convert flat address fields to nested address object
      if (
        value.address ||
        value.city ||
        value.state ||
        value.country ||
        value.postalCode
      ) {
        const addressObj = {
          street: value.address || "",
          city: value.city || "",
          state: value.state || "",
          country: value.country || "",
          postalCode: value.postalCode || "",
        };
        value.address = addressObj;

        // Remove the flat fields after converting to nested object
        delete value.city;
        delete value.state;
        delete value.country;
        delete value.postalCode;
      }

      // Process uploaded files if any
      const fileData = processUploadedFiles((req as any).files);

      // Process documents with their types
      let documentTypes = [];
      if (formData.documentTypes) {
        documentTypes =
          typeof formData.documentTypes === "string"
            ? JSON.parse(formData.documentTypes)
            : formData.documentTypes;
      }

      if (fileData.documents && Array.isArray(fileData.documents)) {
        // Add new documents to existing ones
        const existingDocuments = existingStudent.documents || [];
        fileData.documents = fileData.documents.map(
          (doc: any, index: number) => ({
            ...doc,
            documentType: documentTypes?.[index] || "other",
            verified: false,
          }),
        );
        // Merge with existing documents
        fileData.documents = [...existingDocuments, ...fileData.documents];
      }

      // Clean up fileData to remove incomplete objects
      if (fileData.profilePicture && !fileData.profilePicture.fileName) {
        delete fileData.profilePicture;
      }

      // Convert majorSubjectsIds strings to ObjectIds if provided (deprecated)
      if (value.majorSubjectsIds && Array.isArray(value.majorSubjectsIds)) {
        value.majorSubjectsIds = value.majorSubjectsIds
          .filter((id: string) => mongoose.Types.ObjectId.isValid(id))
          .map((id: string) => new mongoose.Types.ObjectId(id));
      }
      // Convert majorCourseIds strings to ObjectIds if provided
      if (value.majorCourseIds && Array.isArray(value.majorCourseIds)) {
        value.majorCourseIds = value.majorCourseIds
          .filter((id: string) => mongoose.Types.ObjectId.isValid(id))
          .map((id: string) => new mongoose.Types.ObjectId(id));
      }

      // Update student profile (excluding schoolId as it should be immutable)
      const { schoolId, ...updateData } = value;
      Object.assign(existingStudent, updateData, fileData);
      await existingStudent.save();

      // Populate school details after saving
      await existingStudent.populate(
        "schoolId",
        "schoolName establishmentYear phone website email address description logo status isVerified",
      );

      logger.info(`Student profile updated for user: ${authReq.user.email}`);

      res.status(200).json({
        success: true,
        message: "Student profile updated successfully",
        data: {
          student: {
            id: existingStudent._id,
            userId: existingStudent.userId,
            schoolId: existingStudent.schoolId,
            school: existingStudent.schoolId, // Populated school details
            studentId: existingStudent.studentId,
            fullName: existingStudent.fullName,
            dateOfBirth: existingStudent.dateOfBirth,
            gender: existingStudent.gender,
            phone: existingStudent.phone,
            address: existingStudent.address,
            profilePicture: existingStudent.profilePicture,
            documents: existingStudent.documents,
            academicLevel: existingStudent.academicLevel,
            academicYear: existingStudent.academicYear,
            majorSubjectsIds: existingStudent.majorSubjectsIds,
            majorCourseIds: existingStudent.majorCourseIds,
            minorSubjects: existingStudent.minorSubjects,
            guardians: existingStudent.guardians,
            isApproved: existingStudent.isApproved,
            status: existingStudent.status,
            updatedAt: existingStudent.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Student profile update error:", error);

      if (error instanceof mongoose.Error.ValidationError) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.message,
        } as APIResponse);
      } else {
        res.status(500).json({
          success: false,
          message: "Internal server error. Please try again later.",
        } as APIResponse);
      }
    }
  }

  /**
   * Update admin profile (basic user info only)
   */
  async updateAdminProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only admin users can update admin profiles
      if (userRole !== "admin") {
        res.status(403).json({
          success: false,
          message: "Only admin users can update admin profiles",
        } as APIResponse);
        return;
      }

      const user = await User.findById(userId);
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      // Parse form data
      const formData = { ...req.body };

      // Parse address if it's a JSON string
      if (formData.address && typeof formData.address === "string") {
        try {
          formData.address = JSON.parse(formData.address);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      // Basic validation for admin profile update
      const updateAdminValidation = teacherProfileValidation.fork(
        Object.keys(teacherProfileValidation.describe().keys),
        schema => schema.optional(),
      );

      // Validate request body
      const { error, value } = updateAdminValidation.validate(formData);
      if (error) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.details[0].message,
        } as APIResponse);
        return;
      }

      // Convert flat address fields to nested address object
      if (
        value.address ||
        value.city ||
        value.state ||
        value.country ||
        value.postalCode
      ) {
        const addressObj = {
          street: value.address || "",
          city: value.city || "",
          state: value.state || "",
          country: value.country || "",
          postalCode: value.postalCode || "",
        };
        value.address = addressObj;

        // Remove the flat fields after converting to nested object
        delete value.city;
        delete value.state;
        delete value.country;
        delete value.postalCode;
      }

      // Process uploaded files if any
      const fileData = processUploadedFiles(
        (req as any).files || { profileImage: [(req as any).file] },
      );

      // Update user profile
      Object.assign(user, value, fileData);
      await user.save();

      logger.info(`Admin profile updated for user: ${authReq.user.email}`);

      res.status(200).json({
        success: true,
        message: "Admin profile updated successfully",
        data: {
          user: {
            id: user._id,
            email: user.email,
            role: user.role,
            status: user.status,
            isEmailVerified: user.isEmailVerified,
            isProfileCompleted: user.isProfileCompleted,
            isNotificationEnabled: user.isNotificationEnabled,
            updatedAt: user.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Admin profile update error:", error);

      if (error instanceof mongoose.Error.ValidationError) {
        res.status(400).json({
          success: false,
          message: "Validation failed",
          error: error.message,
        } as APIResponse);
      } else {
        res.status(500).json({
          success: false,
          message: "Internal server error. Please try again later.",
        } as APIResponse);
      }
    }
  }

  /**
   * Check profile creation status
   */
  async checkProfileStatus(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Find user
      const user = await User.findById(userId);
      if (!user) {
        res.status(404).json({
          success: false,
          message: "User not found",
        } as APIResponse);
        return;
      }

      // Check profile completion status based on role
      let profileExists = false;
      let personalProfileExists = false;

      switch (userRole) {
        case "school": {
          const schoolProfile = await School.findOne({ userId });
          const personalProfile = await Personal.findOne({ userId });
          profileExists = !!schoolProfile;
          personalProfileExists = !!personalProfile;
          break;
        }
        case "student": {
          const studentProfile = await Student.findOne({ userId });
          profileExists = !!studentProfile;
          break;
        }
        case "teacher": {
          const teacherProfile = await Teacher.findOne({ userId });
          profileExists = !!teacherProfile;
          break;
        }
        default: {
          // Admin users don't need profiles
          profileExists = true;
          break;
        }
      }

      res.status(200).json({
        success: true,
        message: "Profile status retrieved successfully",
        data: {
          user: {
            id: user._id,
            email: user.email,
            role: user.role,
            status: user.status,
            isEmailVerified: user.isEmailVerified,
            isProfileCompleted: user.isProfileCompleted,
          },
          profileStatus: {
            hasProfile: profileExists,
            hasPersonalProfile: personalProfileExists,
            needsPersonalProfile:
              userRole === "school" && !personalProfileExists,
            needsRoleProfile: userRole !== "admin" && !profileExists,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Check profile status error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Handle file upload errors
   */
  handleUploadError(error: any, req: Request, res: Response, next: any): void {
    const errorInfo = handleUploadError(error);
    res.status(errorInfo.status).json({
      success: false,
      message: errorInfo.message,
    } as APIResponse);
  }
}

export const profileController = new ProfileController();
